import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { get, throttle } from 'lodash';

import Platform from '../../components/Platform';
import Sidebar from '../../components/Sidebar';
import { ExpandControls, BlockControls } from '../../components/Controls';
import ErrorModal from '../../components/Modal/Error';
import ExitModal from '../../components/Modal/Exit';
import RecordListModal from '../../components/Modal/RecordList';
import SettingsModal from '../../components/Modal/Settings';
import PrintSettingsModal from '../../components/Modal/PrintSettings';
import Loading from '../../components/Loading';
import ModalPrintLoading from '../../components/Modal/PrintLoading';

import { IDispatch, IRootState } from '../../modules/stores';

import { Container, ContentContainer } from './styled';
import { ElementTypes } from '../../types/element';
import { IURLParams, ExitType } from '../../modules/core/types';
import { postMessage, safeNumber } from '../../helpers';
import { PostMessageEvent } from '../../types/message';
import { KEY_CODES } from '../../constants/keyCodes';
import { buildStorageKey } from '../../helpers/storage';

const WINDOW_THRESHOLD = 1100;
const WINDOW_PREVIEW_THRESHOLD = 600;
const tagsToAvoidDeletion = ['INPUT', 'BUTTON', 'TEXTAREA'];
const POLL_TIME_INTERVAL = safeNumber(
  get(process.env, 'REACT_APP__POLL_TIME_INTERVAL', undefined),
  60_000,
);
const POLL_DELAY_OFFSET = safeNumber(
  get(process.env, 'REACT_APP__POLL_DELAY_OFFSET', undefined),
  10_000,
);

type IConnectProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

interface IProps extends IConnectProps {
  urlParams: IURLParams;
}

const TemplatePage: React.FC<IProps> = ({
  urlParams,
  isLoadingPrinting,
  isViewLoading,
  isCoreLoading,
  coreError,
  isExpand,
  isEditingMode,
  isInTemplate,
  editingSpaceType,
  editingSpaceId,
  isViewEmpty,
  hasChanged,
  exitType,
  getAllData,
  refreshData,
  refreshAppConfig,
  setExitType,
  removeSpace,
  removeEditingSpace,
  saveSpaceConfigsByTemplate,
  duplicateSpace,
  removeCoreError,
  syncAppView,
}) => {
  const [coreLoading, setCoreLoading] = useState<boolean>(true);
  const [isSmallWindow, setIsSmallWindow] = useState<boolean>(false);
  const [isTinyWindow, setIsTinyWindow] = useState<boolean>(false);
  const [cmdKeyPressed, setCmdKeyPressed] = useState<boolean>(false);
  const [showRecordList, setShowRecordList] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showPrintSettings, setShowPrintSettings] = useState<boolean>(false);

  const handleKeyDown = useCallback(
    (evt: KeyboardEvent) => {
      if (!isExpand) return;

      switch (evt.keyCode) {
        case KEY_CODES.BACKSPACE:
        case KEY_CODES.DELETE:
          if (!editingSpaceId) return;

          try {
            if (!_isFocusedOnAnyInput()) {
              removeSpace();
            }
          } catch (e) {}
          break;
        case KEY_CODES.COMMAND:
        case KEY_CODES.COMMAND2:
        case KEY_CODES.CTRL:
          if (!isInTemplate || showRecordList) return;
          setCmdKeyPressed(true);
          break;
        case KEY_CODES.ESC:
          if (isEditingMode) removeEditingSpace();

          break;

        case KEY_CODES.D:
          if (!isInTemplate || !cmdKeyPressed || showRecordList) return;
          evt.preventDefault();

          if (editingSpaceId) duplicateSpace(editingSpaceId);
          break;
        case KEY_CODES.F:
          if (isViewEmpty || !isInTemplate || !cmdKeyPressed || showRecordList) return;
          evt.preventDefault();

          setShowRecordList(true);
          setCmdKeyPressed(false);
          break;
        case KEY_CODES.P:
          if (!isInTemplate || !cmdKeyPressed || showRecordList) return;
          evt.preventDefault();

          setShowPrintSettings(true);
          setCmdKeyPressed(false);
          break;
        default:
          break;
      }

      if (
        ((evt.keyCode >= KEY_CODES._0 && evt.keyCode <= KEY_CODES._9) ||
          (evt.keyCode >= KEY_CODES.A && evt.keyCode <= KEY_CODES.Z)) &&
        editingSpaceType === ElementTypes.StaticText &&
        !_isFocusedOnAnyInput()
      ) {
        const staticInput = document.getElementById('static-text-input');

        if (staticInput) staticInput.focus();
      }
    },
    [
      showRecordList,
      cmdKeyPressed,
      isExpand,
      isEditingMode,
      isViewEmpty,
      isInTemplate,
      editingSpaceId,
      editingSpaceType,
      duplicateSpace,
      removeEditingSpace,
      removeSpace,
    ],
  );

  const handleKeyUp = useCallback(
    (evt: KeyboardEvent) => {
      if (!isExpand) return;

      switch (evt.keyCode) {
        case KEY_CODES.COMMAND:
        case KEY_CODES.COMMAND2:
        case KEY_CODES.CTRL:
          setCmdKeyPressed(false);
          break;
        default:
          break;
      }
    },
    [isExpand],
  );

  const handleWindowResize = useCallback(() => {
    if (isSmallWindow && window.innerWidth > WINDOW_THRESHOLD) {
      setIsSmallWindow(false);
    }
    if (!isSmallWindow && window.innerWidth <= WINDOW_THRESHOLD) {
      setIsSmallWindow(true);
    }

    if (isTinyWindow && window.innerWidth > WINDOW_PREVIEW_THRESHOLD) {
      setIsTinyWindow(false);
    }
    if (!isTinyWindow && window.innerWidth <= WINDOW_PREVIEW_THRESHOLD) {
      setIsTinyWindow(true);
    }
  }, [isSmallWindow, isTinyWindow]);

  const handleClick = useCallback(
    (e: any) => {
      if (
        isEditingMode &&
        e.target &&
        e.target.id &&
        (e.target.id === 'capture' || e.target.id === 'content-scroll')
      ) {
        removeEditingSpace();
      }
    },
    [isEditingMode, removeEditingSpace],
  );

  // postMessages handler
  const handleMessage = useCallback(
    async (e: MessageEvent) => {
      const eventName = e.data.eventName;

      switch (eventName) {
        case PostMessageEvent.CLOSE_MODAL_CHECK:
          if (!hasChanged) {
            onConfirmClose(ExitType.CLOSE);
          } else {
            setExitType(ExitType.CLOSE);
          }
          break;
        case PostMessageEvent.REFRESH_APP_CONFIG:
          await refreshAppConfig();
          break;
        case PostMessageEvent.REFRESH_MODAL_CHECK:
          if (!hasChanged) {
            postMessage({ eventName: PostMessageEvent.REFRESH_MODAL });
          } else {
            setExitType(ExitType.REFRESH);
          }
          break;
      }
    },
    [hasChanged, refreshAppConfig, setExitType],
  );

  // local storage handler
  const handleStorage = useCallback(
    (e: StorageEvent) => {
      if (e.key === buildStorageKey(urlParams.blockId)) {
        syncAppView();
      }
    },
    [urlParams.blockId, syncAppView],
  );

  const onConfirmClose = (exitType?: ExitType | null) => {
    switch (exitType) {
      case ExitType.CLOSE:
        postMessage({ eventName: PostMessageEvent.CLOSE_MODAL });
        break;
      case ExitType.REFRESH:
        postMessage({ eventName: PostMessageEvent.REFRESH_MODAL });
        break;
      default:
        postMessage({ eventName: PostMessageEvent.CLOSE_MODAL });
    }
  };

  const onCloseWithSave = async (exitType: ExitType | null) => {
    await saveSpaceConfigsByTemplate();
    onConfirmClose(exitType);
  };

  const onSubmitConfirmModal = () => {
    removeCoreError();
  };

  useEffect(() => {
    getAllData({ urlParams });

    let interval: number;
    const timeout = window.setTimeout(() => {
      interval = window.setInterval(() => {
        refreshData({ urlParams });
      }, POLL_TIME_INTERVAL);
    }, (urlParams.index * POLL_DELAY_OFFSET) % 30_000);

    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
    };
  }, [urlParams, getAllData, refreshData]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    document.addEventListener('click', handleClick);
    window.addEventListener('resize', throttle(handleWindowResize, 500));
    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener('click', handleClick);
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
    };
  }, [handleKeyDown, handleKeyUp, handleClick, handleWindowResize, handleMessage, handleStorage]);

  useEffect(() => {
    if (!isCoreLoading && coreLoading) {
      setCoreLoading(false);

      // lets parent know loading is finished if in iframe
      postMessage({
        eventName: PostMessageEvent.SEND_READY_STATUS,
        data: {
          blockId: urlParams.blockId,
          isExpand,
        },
      });
    }
  }, [urlParams.blockId, isExpand, isCoreLoading, coreLoading]);

  return (
    <Container>
      {coreLoading || isViewLoading ? (
        <Loading />
      ) : (
        <>
          {isEditingMode && <Sidebar />}
          <ContentContainer>
            {isExpand ? (
              <ExpandControls
                openRecordListModal={() => setShowRecordList(true)}
                openSettingsModal={() => setShowSettings(true)}
                openPrintSettingsModal={() => setShowPrintSettings(true)}
                isSmallWindow={isSmallWindow}
                isTinyWindow={isTinyWindow}
              />
            ) : (
              <BlockControls isSmallWindow={isSmallWindow} isTinyWindow={isTinyWindow} />
            )}
            <Platform />
          </ContentContainer>
        </>
      )}
      {!!coreError && isExpand && (
        <ErrorModal
          onClose={onSubmitConfirmModal}
          titleTextId={coreError.titleId || 'core.error.header'}
          descriptionTextId={coreError.msgId || 'core.error.body'}
        />
      )}
      {exitType && (
        <ExitModal
          onClose={() => setExitType(null)}
          onCloseWithSave={() => onCloseWithSave(exitType)}
          onCloseWithoutSave={() => onConfirmClose(exitType)}
        />
      )}
      {showRecordList && <RecordListModal onClose={() => setShowRecordList(false)} />}
      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
      {showPrintSettings && <PrintSettingsModal onClose={() => setShowPrintSettings(false)} />}
      {isLoadingPrinting && <ModalPrintLoading />}
    </Container>
  );
};

const _isFocusedOnAnyInput = () => {
  /**
   * Check if the user is NOT focused on the drawer (perhaps they are inputting something)
   * The second check is just another safety check in case the user is on an input
   * that is in a modal or something.
   */
  if (tagsToAvoidDeletion.includes(document.activeElement!.tagName.toUpperCase())) return true;

  const drawer = document.getElementById('main-drawer');
  return drawer!.contains(document.activeElement);
};

const mapState = ({ core, space }: IRootState) => ({
  isCoreLoading: core.isLoading,
  isInTemplate: core.currentBlockId !== null,
  isViewLoading: core.isLoadingView,
  isViewEmpty: core.isViewEmpty,
  isExpand: core.isExpand,
  isEditingMode: space.isEditingMode,
  isLoadingPrinting: space.isLoadingPrinting,
  isPortrait: space.isPortrait,
  hasChanged: space.hasChanged,
  editingSpaceId: space.editingSpaceId,
  editingSpaceType: space.editingSpaceId && space.byId[space.editingSpaceId].type,
  coreError: core.error,
  exitType: core.exitType,
});

const mapDispatch = ({ core, space }: IDispatch) => ({
  getAllData: core.getAllData,
  refreshData: core.refreshData,
  refreshAppConfig: core.refreshAppConfig,
  setError: core.setError,
  setExitType: core.setExitType,
  removeCoreError: () => core.setError(null),
  syncAppView: core.syncAppView,
  duplicateSpace: space.duplicateSpace,
  saveSpaceConfigsByTemplate: space.saveSpaceConfigsByTemplate,
  setEditingSpaceId: space.setEditingSpaceId,
  removeSpace: space.removeSpace,
  removeEditingSpace: () => space.removeEditingSpace(),
});

export default connect(
  mapState,
  // @ts-ignore
  mapDispatch,
)(TemplatePage);
